/**
 * Global Search Provider Interface
 *
 * This abstraction allows swapping search backends (Mock → Firestore → Typesense)
 * without touching UI components.
 */

export type EntityType =
  | 'vehicle'
  | 'customer'
  | 'salesInvoice'
  | 'purchaseInvoice'
  | 'workOrder'
  | 'part';

export interface GlobalSearchResult {
  entityType: EntityType;
  id: string;
  teamId: string;
  title: string;
  subtitle?: string;
  relatedIds?: {
    vehicleId?: string;
    customerId?: string;
  };
  openTarget: {
    route: string;
    dialog: string;
    idParam: string;
  };
}

export interface SearchOptions {
  /** Restrict search to a specific entity type */
  scope?: EntityType;
}

export interface SearchProvider {
  /**
   * Search across all entity types (or scoped to one)
   * @param query - Search query string
   * @param teamId - Team ID to scope results
   * @param options - Optional search options (scope, etc.)
   * @returns Promise resolving to array of search results
   */
  search(query: string, teamId: string, options?: SearchOptions): Promise<GlobalSearchResult[]>;
}

/**
 * Entity type labels for display
 */
export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  vehicle: 'Voertuig',
  customer: 'Klant',
  salesInvoice: 'Verkoopfactuur',
  purchaseInvoice: 'Inkoopfactuur',
  workOrder: 'Werkorder',
  part: 'Onderdeel',
};

/**
 * Entity type icons (Lucide icon names)
 */
export const ENTITY_TYPE_ICONS: Record<EntityType, string> = {
  vehicle: 'Car',
  customer: 'Users',
  salesInvoice: 'FileText',
  purchaseInvoice: 'Receipt',
  workOrder: 'Wrench',
  part: 'Package',
};
